import { useEffect, useRef, useState } from "react";
import useMicSettings from "zustand-store/mic";
import { ReactComponent as MicIcon } from "assets/svgs/mic.svg";
import { WORKLETCODE_PLACEHOLDER } from "enums";
import useWaveform from "utils/hooks/use-waveform";

const COUNTDOWN_SECONDS = 7;
const WS_URL = `${process.env.REACT_APP_DEEPGRAM_WS_URL}/ws/live_transcribe`;

function getCognitoIdToken() {
  const key = Object.keys(localStorage).find(
    (k) =>
      k.startsWith("CognitoIdentityServiceProvider.") && k.endsWith(".idToken")
  );
  return key ? localStorage.getItem(key) : null;
}

const MicTest = ({ className }) => {
  const { inputDevice } = useMicSettings();

  const [phase, setPhase] = useState("idle"); // 'idle' | 'recording' | 'done' | 'error'
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [transcript, setTranscript] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const workletNodeRef = useRef(null);
  const streamRef = useRef(null);
  const countdownRef = useRef(null);
  // Committed finals and the in-progress interim are tracked apart — see onmessage.
  const committedRef = useRef("");
  const liveRef = useRef("");

  /* Waveform. The mirrored-spectrum renderer from Create Notes, rather than the
     bespoke one that used to live here — that one drew `rgba(51, 187, 207, …)`
     straight onto the canvas, which is the OLD brand's cyan. The Phase 4-5
     colour migration only swept Tailwind classes, so a hardcoded canvas fill
     string was invisible to it and survived the rebrand.

     The hook needs the analyser as a value, not a ref, so it can re-run when one
     appears. */
  const canvasRef = useRef(null);
  const [analyser, setAnalyser] = useState(null);

  // Must come AFTER canvasRef and analyser are declared. Placed above them it
  // throws "Cannot access 'canvasRef' before initialization" at runtime — a
  // temporal dead zone error, which compiles clean and only shows up on screen.
  // 50px tall: below ~56px the spec drops the peak-hold caps and thins the bars.
  useWaveform({ canvasRef, analyser, isRecording: phase === "recording", bars: 9, caps: false });

  const cleanup = () => {
    clearInterval(countdownRef.current);

    // Dropping the analyser stops the hook's loop and settles the canvas.
    setAnalyser(null);

    wsRef.current?.close();
    wsRef.current = null;

    workletNodeRef.current?.disconnect();
    workletNodeRef.current = null;

    if (audioContextRef.current) {
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
      audioContextRef.current = null;
    }

    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  // Cleanup on unmount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => cleanup(), []);

  // Cancel if device changes mid-recording
  useEffect(() => {
    if (phase === "recording") {
      cleanup();
      setPhase("idle");
      setCountdown(COUNTDOWN_SECONDS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputDevice?.deviceId]);

  const setupAudioProcessing = async (stream, ws) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)(
        { sampleRate: 16000 }
      );
      audioContextRef.current = audioContext;

      // Analyser for waveform visualization. useWaveform sizes its own buffer
      // and raises fftSize; this node has no other consumer.
      const analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = 256;
      setAnalyser(analyserNode);

      const blob = new Blob([WORKLETCODE_PLACEHOLDER], {
        type: "application/javascript",
      });
      const workletUrl = URL.createObjectURL(blob);
      await audioContext.audioWorklet.addModule(workletUrl);

      const source = audioContext.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(audioContext, "pcm-processor");
      workletNodeRef.current = workletNode;

      workletNode.port.onmessage = (event) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        }
      };

      // Fan out: source → analyser (visualization) + source → worklet (PCM stream)
      source.connect(analyserNode);
      source.connect(workletNode);
      workletNode.connect(audioContext.destination);
    } catch (err) {
      console.error("Audio processing setup failed:", err);
      cleanup();
      setErrorMsg("Audio processing failed. Please try again.");
      setPhase("error");
    }
  };

  const handleStart = async () => {
    setPhase("recording");
    setCountdown(COUNTDOWN_SECONDS);
    setTranscript("");
    setErrorMsg("");
    committedRef.current = "";
    liveRef.current = "";

    try {
      const constraints = inputDevice?.deviceId
        ? { audio: { deviceId: { exact: inputDevice.deviceId } } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      const idToken = getCognitoIdToken();
      const ws = new WebSocket(`${WS_URL}?token=${idToken}`);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => setupAudioProcessing(stream, ws);

      /* Deepgram interims are CUMULATIVE REVISIONS of the utterance in progress,
         not increments. Each one restates everything said since the last final.

         This used to append every interim to the transcript, so saying
         "Hi, how are you" printed "Hi. How are Hey. How are doing? Hey. How you
         doing? Hey. How you doing? …" — the same phrase over and over as
         Deepgram refined its guess. An interim REPLACES the live tail.

         Finals arrive as `cleaned_turns` and are what actually gets committed;
         this panel ignored them entirely, so it was showing the raw guesses and
         never the cleaned text. */
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);

          if (data.type === "interim" && data.text) {
            liveRef.current = data.text;
          } else if (data.type === "cleaned_turns") {
            const text = (data.turns || [])
              .map((turn) => (turn.text || "").trim())
              .filter(Boolean)
              .join(" ");
            if (text) {
              committedRef.current = `${committedRef.current} ${text}`.trim();
            }
            // The finals that produced this block supersede the live guess.
            liveRef.current = "";
          } else {
            return;
          }

          setTranscript(`${committedRef.current} ${liveRef.current}`.trim());
        } catch {}
      };

      ws.onerror = () => {
        cleanup();
        setErrorMsg("Connection failed. Please check your internet and try again.");
        setPhase("error");
      };

      let remaining = COUNTDOWN_SECONDS;
      countdownRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining === 0) {
          cleanup();
          setPhase("done");
        }
      }, 1000);
    } catch (err) {
      console.error("Mic test failed:", err);
      setErrorMsg(
        err.name === "NotAllowedError"
          ? "Microphone access denied. Please allow microphone permissions and try again."
          : "Could not access microphone. Please check your settings and try again."
      );
      setPhase("error");
    }
  };

  const handleReset = () => {
    setPhase("idle");
    setCountdown(COUNTDOWN_SECONDS);
    setTranscript("");
    setErrorMsg("");
    committedRef.current = "";
    liveRef.current = "";
  };

  const isIdle = phase === "idle";
  const isActive = phase === "recording";
  const isDone = phase === "done";
  const isError = phase === "error";

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {/* Controls row */}
      <div className="flex flex-row items-center gap-4 flex-wrap">
        {/* Idle / Done / Error: action button */}
        {(isIdle || isDone || isError) && (
          <button
            onClick={isIdle ? handleStart : handleReset}
            className="flex items-center gap-2 text-[13px] font-semibold text-accent bg-shade-50 border border-accent/30 rounded-full px-4 py-2 transition-colors"
          >
            <MicIcon />
            {isIdle ? "Start listening" : "Try Again"}
          </button>
        )}

        {/* Recording: countdown ring + waveform */}
        {isActive && (
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-shade-50 opacity-30 animate-ping" />
              <span className="relative w-12 h-12 rounded-full bg-shade-50 flex items-center justify-center text-xl font-bold text-ink">
                {countdown}
              </span>
            </div>
            {/* No width/height attributes — useWaveform sizes the backing store
                to devicePixelRatio from the CSS box every frame. */}
            <canvas ref={canvasRef} aria-hidden="true" className="block h-[50px] w-[200px]" />
            <span className="text-sm text-muted font-medium">Recording…</span>
          </div>
        )}
      </div>

      {/* Error message — sits above the fixed-height transcript box */}
      {isError && errorMsg && (
        <p className="text-sm text-danger font-medium">{errorMsg}</p>
      )}

      {/* Transcript box — always rendered so height stays fixed */}
      <div className="flex flex-col gap-1 w-full">
        <p className="text-sm font-semibold text-muted">Transcript</p>
        <textarea
          readOnly
          value={transcript}
          placeholder={
            isIdle
              ? "Press 'Start listening' and say something."
              : isActive
              ? "Transcribing your audio live…"
              : ""
          }
          className="w-full h-36 p-3 border border-rule-strong rounded-ns bg-shade-50 text-sm text-ink resize-none focus:outline-none cursor-default"
        />
      </div>
    </div>
  );
};

export default MicTest;
